import { useRequiredContext } from "@redotech/react-util/context";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect } from "react";
import { RedoAdminRpcClientContext } from "../../../../app/redo-admin-rpc-client-provider";
import { useRateCalculator } from "./hooks";
import * as styles from "./rate-calculator-wizard.module.css";
import { CarrierSelectionStep } from "./steps/carrier-selection-step";
import { DataInputStep } from "./steps/data-input-step";
import { PricingConfigStep } from "./steps/pricing-config-step";
import type { WizardStep } from "./types";

const STEP_TITLES: Record<WizardStep, string> = {
  1: "Carrier Eligibility",
  2: "Data Input",
  3: "Pricing",
};

const StepIndicator = memo(function StepIndicator({
  currentStep,
}: {
  currentStep: WizardStep;
}) {
  const steps: WizardStep[] = [1, 2, 3];

  return (
    <Flex align="center" gap="sm" justify="center" w="full">
      {steps.map((step, index) => {
        const isCompleted = step < currentStep;
        const isCurrent = step === currentStep;

        return (
          <Flex align="center" gap="sm" key={step}>
            <Flex
              align="center"
              className={`${styles.stepIndicatorCircle} ${isCompleted ? styles.stepIndicatorCircleCompleted : styles.stepIndicatorCircleDefault}`}
              justify="center"
              radius="full"
            >
              <Text
                className={
                  isCompleted
                    ? styles.stepIndicatorTextCompleted
                    : styles.stepIndicatorTextDefault
                }
                fontSize="sm"
                fontWeight="semibold"
              >
                {step}
              </Text>
            </Flex>
            <Text
              color={isCurrent ? "primary" : "tertiary"}
              fontSize="sm"
              fontWeight={isCurrent ? "semibold" : "regular"}
            >
              {STEP_TITLES[step]}
            </Text>
            {index < steps.length - 1 && (
              <Flex
                bgColor={isCompleted ? "success-primary" : "disabled"}
                className={styles.stepConnectorLine}
              />
            )}
          </Flex>
        );
      })}
    </Flex>
  );
});

const WizardContent = memo(function WizardContent() {
  const { state } = useRateCalculator();

  switch (state.step) {
    case 1:
      return <CarrierSelectionStep />;
    case 2:
      return <DataInputStep />;
    case 3:
      return <PricingConfigStep />;
    default:
      return null;
  }
});

export const RateCalculatorWizard = memo(function RateCalculatorWizard() {
  const { state, dispatch } = useRateCalculator();
  const client = useRequiredContext(RedoAdminRpcClientContext);

  useEffect(() => {
    if (
      !state.isOpen ||
      state.baselineRateTables ||
      state.baselineRateTablesLoading
    ) {
      return;
    }

    dispatch({ type: "SET_BASELINE_RATE_TABLES_LOADING", loading: true });

    client
      .getBaselineRateTables({})
      .then((result) => {
        if (result.rateTables) {
          dispatch({
            type: "SET_BASELINE_RATE_TABLES",
            rateTables: result.rateTables,
          });
        } else {
          dispatch({
            type: "SET_BASELINE_RATE_TABLES_ERROR",
            error: "No rate tables found in global metadata",
          });
        }
      })
      .catch((err) => {
        dispatch({
          type: "SET_BASELINE_RATE_TABLES_ERROR",
          error:
            err instanceof Error ? err.message : "Failed to load rate tables",
        });
      });
  }, [
    state.isOpen,
    state.baselineRateTables,
    state.baselineRateTablesLoading,
    client,
    dispatch,
  ]);

  if (!state.isOpen) {
    return null;
  }

  return (
    <RedoBaseModal
      isOpen={state.isOpen}
      onModalCloseRequested={() => {
        dispatch({ type: "CLOSE_WIZARD" });
        dispatch({ type: "RESET" });
      }}
      otherClasses={[styles.wizardModal]}
    >
      <Flex className={styles.wizardContainer} dir="column" gap="lg">
        <Flex align="center" dir="column" gap="md" pt="lg" px="lg">
          <Text fontSize="xl" fontWeight="semibold">
            OMS Pricing Tool
          </Text>
          <StepIndicator currentStep={state.step} />
        </Flex>
        <Flex className={styles.wizardContent} dir="column" grow={1} p="lg">
          <WizardContent />
        </Flex>
      </Flex>
    </RedoBaseModal>
  );
});
